import { CountUp } from 'countup.js';

let observer;
let loaderObserver;

/**
 * Initializes CountUp animations for elements matching the selector.
 * Uses IntersectionObserver to trigger animation when element enters viewport.
 */
export function initializeCountUp(container = document) {
    const scope = container || document;
    const statsNumbers = scope.querySelectorAll('.stats[data-animate="true"] .stat__number');

    if (statsNumbers.length === 0) return;

    // Disconnect existing observer if any
    if (observer) {
        observer.disconnect();
    }
    
    // Disconnect existing loader observer if any
    if (loaderObserver) {
        loaderObserver.disconnect();
        loaderObserver = null;
    }

    // Check if page is loading
    if (document.body.classList.contains('is-loading')) {
        loaderObserver = new MutationObserver(() => {
            if (!document.body.classList.contains('is-loading')) {
                startObserver(statsNumbers);
                if (loaderObserver) {
                    loaderObserver.disconnect();
                    loaderObserver = null;
                }
            }
        });
        loaderObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } else {
        startObserver(statsNumbers);
    }
}

function startObserver(statsNumbers) {
    const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.15 // Trigger when 15% visible
    };

    observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target;
                animateCountUp(element);
                obs.unobserve(element);
            }
        });
    }, observerOptions);

    statsNumbers.forEach(number => {
        if (!number.classList.contains('js-countup-init')) {
            number.classList.add('js-countup-init');
            // Store original text content as the target value
            number.dataset.countupValue = number.textContent.trim();
            observer.observe(number);
        }
    });
}

function animateCountUp(element) {
    const rawValue = element.dataset.countupValue || element.textContent.trim();
    // Remove commas for parsing
    const endValue = parseFloat(rawValue.replace(/,/g, ''));
    
    if (isNaN(endValue)) return;

    const useThousands = element.dataset.thousands === 'true';
    const delay = parseFloat(element.dataset.delay) || 0;
    
    let decimalPlaces = 0;
    if (rawValue.includes('.')) {
        decimalPlaces = rawValue.split('.')[1].length;
    }

    const options = {
        decimalPlaces: decimalPlaces,
        duration: 2.5,
        useEasing: true,
        useGrouping: useThousands,
        separator: useThousands ? ',' : '',
    };

    const countUp = new CountUp(element, endValue, options);

    if (!countUp.error) {
        if (delay > 0) {
            // Reset to 0 immediately so it doesn't show the component's final value during the delay
            element.textContent = (0).toFixed(decimalPlaces);
            setTimeout(() => {
                countUp.start();
            }, delay * 1000);
        } else {
            countUp.start();
        }
    } else {
        console.error('CountUp error:', countUp.error);
        // Fallback: ensure number is visible
        element.textContent = rawValue;
    }
}

export function destroyCountUp() {
    if (observer) {
        observer.disconnect();
        observer = null;
    }
    if (loaderObserver) {
        loaderObserver.disconnect();
        loaderObserver = null;
    }
}